import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { BOOKING_FLOW_ENTRY, StateName } from './types';
import { useSelectActiveLocation } from '~/hooks/redux/location';
import { useSelectNewBooking } from '~/hooks/redux/newBooking';
import { analyticsTrackEvent } from '~/core/analytics';
import { BOOKING_FLOW_STEP } from '~/core/analytics/events';
import { isOnlineWaitlistAtNonTelemedLocation } from '~/core/util/booking';

/** Retrieves the correct version of the booking flow based on whether the KIS-511 H2 2022 Booking Flow
 * flag is enabled */
export const getBookingFlowEntryRoute = (
  isKIS511Enabled: boolean,
  origin: string,
  isPaymentCollectionEnabled: boolean | undefined, // The | undefined is added for parity with isPaymentCollectionDuringBookingEnabled()
  urlSlug?: string // used for nearby module
  // TODO (if login is re-enabled on booking flow): isKIS772Enabled: boolean
) => {
  // Force displaying the old booking flow if the clinic allows for adding a payment card & sign-in is disabled.
  // The Payment collection step requires sign-in to function correctly
  const forceOldBookingFlow = isPaymentCollectionEnabled; // TODO (if login is re-enabled on booking flow): && isKIS772Enabled;

  if (isKIS511Enabled && !forceOldBookingFlow) {
    // Encode the origin path into the query params so we can navigate back to it when exiting
    // the booking flow
    const pathname = urlSlug ?? window.location.pathname;
    return `${BOOKING_FLOW_ENTRY}?s=${origin}&o=${encodeURIComponent(
      `${pathname}${window.location.search}`
    )}`;
  }
  return `/profile?s=${origin}`;
};

export const useAppointmentDateString = () => {
  const { t } = useTranslation();
  const location = useSelectActiveLocation();
  const newBooking = useSelectNewBooking();
  const isOnlineWaitlist = isOnlineWaitlistAtNonTelemedLocation(newBooking.booking, location);

  return useMemo(() => {
    const timeSuffixCode = isOnlineWaitlist ? ', MMMM D' : ', MMMM D [at] h:mm a';
    const parsedDate = moment(newBooking.booking.appointmentTime);

    if (!parsedDate.isValid()) {
      return undefined;
    }

    return moment(newBooking.booking.appointmentTime)
      .tz(location.timeZone)
      .calendar({
        sameDay: `[${t('today.capitalize')}]${timeSuffixCode}`,
        nextDay: `[${t('tomorrow.capitalize')}]${timeSuffixCode}`,
        nextWeek: `dddd${timeSuffixCode}`,
        lastDay: `[${t('yesterday.capitalize')}]${timeSuffixCode}`,
        lastWeek: `[${t('dtFormat.lastDayOfWeek')}]${timeSuffixCode}`,
        sameElse: `dddd${timeSuffixCode}`,
      });
  }, [newBooking.booking.appointmentTime, location.timeZone, t, isOnlineWaitlist]);
};

const StateNamesToAnalyticsEvents: Record<
  StateName,
  typeof BOOKING_FLOW_STEP[keyof typeof BOOKING_FLOW_STEP]
> = {
  RfvEntry: BOOKING_FLOW_STEP.REASON_FOR_VISIT,
  EnterInfoOrLogin: BOOKING_FLOW_STEP.AB_ENTER_INFO_OR_LOGIN,
  NumberLookup: BOOKING_FLOW_STEP.ENTER_PHONE_NUMBER,
  VerifyNumber: BOOKING_FLOW_STEP.ENTER_OTP,
  NewAccountWho: BOOKING_FLOW_STEP.SELECT_PATIENT,
  NewFamilyMember: BOOKING_FLOW_STEP.SELECT_PATIENT,
  SelectPatient: BOOKING_FLOW_STEP.SELECT_PATIENT,
  FamilyMemberDetails: BOOKING_FLOW_STEP.BOOKING_PROFILE,
  NewAccountEnterInfo: BOOKING_FLOW_STEP.BOOKING_PROFILE,
  AddInsurance: BOOKING_FLOW_STEP.INSURANCE,
  Finalize: BOOKING_FLOW_STEP.FINALIZE,
  HomeAddress: BOOKING_FLOW_STEP.HOME_ADDRESS,
  PaymentCollection: BOOKING_FLOW_STEP.PAYMENT_COLLECTION,
  PaymentMethod: BOOKING_FLOW_STEP.PAYMENT_METHOD,
  PhotoIdUpload: BOOKING_FLOW_STEP.PHOTO_ID,
  SelectATime: BOOKING_FLOW_STEP.SELECT_A_TIME,
};

export const useTrackBookingFlowStep = (stateName: StateName, trackingProperties?: any) => {
  useEffect(() => {
    analyticsTrackEvent(StateNamesToAnalyticsEvents[stateName], trackingProperties ?? {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
